/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { EPOCH_DAYS } from './types';

// Common utilities

function leastUpperBound(array: ReadonlyArray<number>, value: number) {
    let l = 0, h = array.length - 1;
    while (l <= h) {
        let m = l + ((h - l) >> 1);
        let mv = array[m];
        let r = mv - value;
        if (r < 0) {
            l = m + 1;
        }
        else if (r > 0) {
            h = m - 1;
        }
        else {
            return m;
        }
    }
    return l;
}


// Date utilities

const DAY_OFFSETS_STANDARD: ReadonlyArray<number> = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334, 365];
const DAY_OFFSETS_LEAP_YEAR: ReadonlyArray<number> = [0, 31, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335, 366];
const MONTHS_PER_YEAR = 12;
const DAYS_PER_1_YEAR = 365;
const DAYS_PER_4_YEARS = 1461;
const DAYS_PER_100_YEARS = 36524;
const DAYS_PER_400_YEARS = 146097;
const DAYS_PER_WEEK = 7;
const EPOCH_DAY_OFFSET = 719162; // days between the EPOCH and 0
const INITIAL_WEEK_DAY = 4; // January 1st, 1970 was a Thursday

/* @internal */ export { MONTHS_PER_YEAR }

// https://tc39.github.io/ecma262/#sec-makeday
/* @internal */ export function getDate(year: number, month: number, day: number) {
    // normalize inputs
    while (month < 1) year--, month += MONTHS_PER_YEAR;
    while (month > MONTHS_PER_YEAR) year++, month -= MONTHS_PER_YEAR;

    const dayOffsets = isLeapYear(year) ? DAY_OFFSETS_LEAP_YEAR : DAY_OFFSETS_STANDARD;
    // https://tc39.github.io/ecma262/#eqn-DaysFromYear
    const date = (year - 1970) * 365 + Math.floor((year - 1969) / 4) - Math.floor((year - 1901) / 100) + Math.floor((year - 1601) / 400) + dayOffsets[month - 1] + day - 1;
    return date as EPOCH_DAYS;
}

/* @internal */ export function isLeapYear(year: number) {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

/* @internal */ export function getDaysInMonth(year: number, month: number) {
    // normalize inputs
    while (month < 1) year-- , month += MONTHS_PER_YEAR;
    while (month > MONTHS_PER_YEAR) year++ , month -= MONTHS_PER_YEAR;
    const dayOffsets = isLeapYear(year) ? DAY_OFFSETS_LEAP_YEAR : DAY_OFFSETS_STANDARD;
    return dayOffsets[month] - dayOffsets[month - 1];
}

/* @internal */ export function getDaysInYear(year: number) {
    return isLeapYear(year) ? DAY_OFFSETS_LEAP_YEAR[MONTHS_PER_YEAR] : DAY_OFFSETS_STANDARD[MONTHS_PER_YEAR];
}

/* @internal */ export function getWeeksInYear(year: number) {
    const yearM1 = year - 1;
    if ((year + Math.floor(year / 4) - Math.floor(year / 100) + Math.floor(year / 400)) % DAYS_PER_WEEK === 4 ||
        (yearM1 + Math.floor(yearM1 / 4) - Math.floor(yearM1 / 100) + Math.floor(yearM1 / 400)) % DAYS_PER_WEEK === 3) {
        return 53;
    }
    return 52;
}

/* @internal */ export interface DateParts {
    year: number;
    month: number;
    day: number;
    dayOfYear: number;
    dayOfWeek: number;
    weekOfYear: number;
}

/* @internal */ export const enum DatePart {
    year,
    dayOfYear,
    month,
    day,
    dayOfWeek,
    weekOfYear,
    all
}

/* @internal */ export function getDatePart<P extends DatePart>(date: EPOCH_DAYS, part: P): P extends DatePart.all ? DateParts : number;
/* @internal */ export function getDatePart(date: EPOCH_DAYS, part: DatePart) {
    const dayOfWeek = part === DatePart.dayOfWeek || part === DatePart.weekOfYear || part === DatePart.all 
        ? ((date + INITIAL_WEEK_DAY) % DAYS_PER_WEEK) || DAYS_PER_WEEK
        : 0;
    if (part === DatePart.dayOfWeek) return dayOfWeek;
    let days = date + EPOCH_DAY_OFFSET;
    const years400 = Math.floor(days / DAYS_PER_400_YEARS);
    days -= years400 * DAYS_PER_400_YEARS;
    let years100 = Math.floor(days / DAYS_PER_100_YEARS);
    if (years100 === 4) years100--;
    days -= years100 * DAYS_PER_100_YEARS;
    const years4 = Math.floor(days / DAYS_PER_4_YEARS);
    days -= years4 * DAYS_PER_4_YEARS;
    let years1 = Math.floor(days / DAYS_PER_1_YEAR);
    if (years1 === 4) years1--;
    const year = years400 * 400 + years100 * 100 + years4 * 4 + years1 + 1;
    if (part === DatePart.year) return year;
    const dayOfYear = days - years1 * DAYS_PER_1_YEAR + 1;
    if (part === DatePart.dayOfYear) return dayOfYear;
    const weekOfYear = part === DatePart.weekOfYear || part === DatePart.all
        ? Math.floor((dayOfYear - dayOfWeek + 10) / DAYS_PER_WEEK)
        : 0;
    if (part === DatePart.weekOfYear) return weekOfYear;
    const dayOffsets = isLeapYear(year) ? DAY_OFFSETS_LEAP_YEAR : DAY_OFFSETS_STANDARD;
    const month = leastUpperBound(dayOffsets, dayOfYear);
    if (part === DatePart.month) return month;
    const day = dayOfYear - dayOffsets[month - 1];
    if (part === DatePart.day) return day;
    return { year, month, day, dayOfYear, dayOfWeek, weekOfYear };
}

function getDayOfYearFromWeekDate(year: number, weekOfYear: number, dayOfWeek: number) {
    // The first week of the year is always the week that contains January 4th.
    // https://en.wikipedia.org/wiki/ISO_week_date#First_week
    // https://en.wikipedia.org/wiki/ISO_week_date#Calculating_a_date_given_the_year,_week_number_and_weekday
    return weekOfYear * DAYS_PER_WEEK + dayOfWeek - (getDatePart(getDate(year, 1, 4), DatePart.dayOfWeek) + 3);
}

/* @internal */ export function getCalendarDateFromWeekDate(year: number, weekOfYear: number, dayOfWeek: number) {
    // normalize inputs
    if (dayOfWeek < 1 || dayOfWeek > DAYS_PER_WEEK) throw new RangeError();
    while (weekOfYear < 1) year--, weekOfYear += getWeeksInYear(year);
    while (weekOfYear > getWeeksInYear(year)) weekOfYear -= getWeeksInYear(year), year++;

    return getCalendarDateFromOrdinalDate(year, getDayOfYearFromWeekDate(year, weekOfYear, dayOfWeek));
}

/* @internal */ export function getCalendarDateFromOrdinalDate(year: number, dayOfYear: number) {
    // normalize inputs
    while (dayOfYear < 1) year--, dayOfYear += getDaysInYear(year);
    while (dayOfYear > getDaysInYear(year)) dayOfYear -= getDaysInYear(year), year++;
    const dayOffsets = isLeapYear(year) ? DAY_OFFSETS_LEAP_YEAR : DAY_OFFSETS_STANDARD;
    const month = leastUpperBound(dayOffsets, dayOfYear)
    const day = dayOfYear - dayOffsets[month - 1];
    return { year, month, day };
}
